import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, X } from 'lucide-react';
import InputMask from 'react-input-mask';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  
  const validateForm = () => {
    const errors = {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: ''
    };
    
    let isValid = true;
    
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName || !formData.phone) {
      setError('Все поля обязательны для заполнения');
      return false;
    }
    
    if (formData.password.length < 6) {
      setError('Пароль должен содержать не менее 6 символов');
      return false;
    }
    
    if (!/^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/.test(formData.phone)) {
      setError('Введите корректный номер телефона');
      return false;
    }
    
    return isValid;
  };

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const registerWithRetry = async (functionUrl: string, body: any, attempt = 0): Promise<Response> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
        body: JSON.stringify(body),
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('Ошибка при отправке заявки. Пожалуйста, попробуйте позже.');
      }

      return response;
    } catch (error) {
      if (attempt < MAX_RETRIES) {
        await wait(Math.pow(2, attempt) * 1000);
        return registerWithRetry(functionUrl, body, attempt + 1);
      }
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rdp-cargo-register`;
      
      console.log('Attempting registration:', {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName, 
        phone: formData.phone
      });

      const response = await registerWithRetry(functionUrl, {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      });

      let data;
      try {
        data = await response.json();
        console.log('Registration response data:', data);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Ошибка при обработке ответа сервера');
      }
      
      if (!response.ok || !data.success) {
        const errorMessage = data.error || response.statusText || 'Ошибка при регистрации';
        console.error('Registration failed:', { status: response.status, error: errorMessage });
        throw new Error(data.error || 'Ошибка при регистрации');
      }
      
      // Store user data in localStorage
      if (data.success) {
        localStorage.setItem('userFirstName', formData.firstName);
        localStorage.setItem('userLastName', formData.lastName);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userPhone', formData.phone);
        localStorage.setItem('userEmail', formData.email);
      
        if (data.userId) {
          localStorage.setItem('userId', data.userId);
        }
        
        setSuccessMessage('Регистрация успешна! Перенаправление на страницу входа...');
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          window.location.href = '/client/dashboard';
        }, 2000);
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.name === 'AbortError') {
        setError('Превышено время ожидания ответа от сервера. Пожалуйста, попробуйте еще раз.');
      } else if (error.message === 'Email already registered') {
        setError('Этот email уже зарегистрирован');
      } else {
        setError(error instanceof Error ? error.message : 'Произошла ошибка при регистрации');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white md:flex">
      {/* Mobile Close Button */}
      <Link 
        to="/"
        className="md:hidden fixed top-16 right-4 w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 bg-gray-100 rounded-full z-50"
      >
        <X className="w-5 h-5" />
      </Link>

      {/* Left Side - Random Image (Desktop Only) */}
      <div className="hidden md:block md:w-2/3 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80"
            alt="Chinese Landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent" />
        </div>
      </div>

      {/* Right Side - Registration Form */}
      <div className="w-full md:w-1/3 min-h-screen md:min-h-0 flex flex-col items-center justify-center p-6 bg-white">
        <div>
          <h2 className="text-3xl font-semibold text-gray-900 mb-2">
            Создать аккаунт
          </h2>
          <p className="text-gray-600">Заполните форму для регистрации</p>
        </div>
        
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-green-500" />
            <span>{successMessage}</span>
          </div>
        )}
        
        <div className="bg-white rounded-xl border border-gray-200 p-8 w-full max-w-md mt-8 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
                className="block w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-[#1B3BA0] focus:border-[#1B3BA0]"
                placeholder="Email"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Пароль
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  minLength={6}
                  className="block w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-[#1B3BA0] focus:border-[#1B3BA0]"
                  placeholder="Пароль (минимум 6 символов)"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                Имя
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
                className="block w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-[#1B3BA0] focus:border-[#1B3BA0]"
                placeholder="Имя"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Фамилия
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                required
                className="block w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-[#1B3BA0] focus:border-[#1B3BA0]"
                placeholder="Фамилия"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Телефон
              </label>
              <InputMask
                id="phone"
                name="phone"
                mask="+7 (999) 999-99-99"
                maskChar="_"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                required
                className="block w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-[#1B3BA0] focus:border-[#1B3BA0]"
                placeholder="+7 (___) ___-__-__"
              />
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">или</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-[#1B3BA0] hover:bg-[#162D7A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1B3BA0] disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              >
                {loading ? 'Регистрация...' : 'Зарегистрироваться'}
              </button>
              
              <Link
                to="/login"
                className="w-full flex justify-center py-2 px-4 mt-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1B3BA0] transition-colors"
              >
                Войти с существующим аккаунтом
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}